import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/services/api";
import { DashboardData, Destination, DestinationHealthInfo, Consultation } from "@/types";
import { getDestinationInfoByCountry } from "@/services/destinations";
import { getDestinationsArray } from "@/utils/dashboardHelpers";
import { apiCache, cacheKeys, cacheTTL } from "@/utils/api-cache";

export function useDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [filteredConsultations, setFilteredConsultations] = useState<Consultation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showRecommendations, setShowRecommendations] = useState<string | null>(null);
  const [destinationInfoMap, setDestinationInfoMap] = useState<Record<string, DestinationHealthInfo>>({});
  const [loadingDestinations, setLoadingDestinations] = useState<Record<string, boolean>>({});
  const [expandedCountries, setExpandedCountries] = useState<Record<string, boolean>>({});
  const [hasFetched, setHasFetched] = useState(false);
  
  // Initialize filters from URL search params
  const urgency = searchParams.get("urgency") || "all";
  const priority = searchParams.get("priority") || "all";
  const status = searchParams.get("status") || "all";
  const search = searchParams.get("search") || "";
  
  const filters = {
    urgency,
    priority,
    status,
    search,
  };
  
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    // Only fetch once when authenticated
    if (!authLoading && isAuthenticated && !hasFetched) {
      fetchDashboard();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, hasFetched]);

  useEffect(() => {
    if (dashboardData?.pendingConsultations) {
      applyFilters();
    }
  }, [dashboardData, urgency, priority, status, search]);

  const fetchDashboard = async () => {
    // Prevent duplicate calls
    if (hasFetched) return;
    
    try {
      setLoading(true);
      setHasFetched(true);
      
      // Get current pharmacy ID from localStorage
      const currentPharmacyId = localStorage.getItem('currentPharmacyId');
      
      // Build query params
      const params: Record<string, string> = {};
      if (currentPharmacyId) {
        params.pharmacy_id = currentPharmacyId;
      }

      // Build cache key based on parameters
      const cacheKey = cacheKeys.dashboard(currentPharmacyId || undefined);
      
      // Use caching for dashboard data
      const data = await apiCache.fetch(
        cacheKey,
        async () => {
          const response = await api.get<DashboardData>("/pharmacy/dashboard", { params });
          return response.data;
        },
        cacheTTL.short // 1 minute - frequently changing data
      );
      
      setDashboardData(data);
      
      // Store current pharmacy ID if returned in response
      if (data.currentPharmacy?.id) {
        localStorage.setItem('currentPharmacyId', data.currentPharmacy.id);
      }
    } catch (err: any) {
      // Check if it's an authentication error
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Authentication failed - let the API interceptor handle it
        // Don't reset hasFetched to prevent retry loop
        setError("Authentication failed");
        return;
      }
      setError("Failed to load dashboard data");
      setHasFetched(false); // Reset on error to allow retry
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyLevel = (departureDate: string): string => {
    if (!departureDate) return "urgent";

    // Parse departure date as local date to avoid timezone issues
    const parts = departureDate.split("T")[0].split("-");
    const departure = new Date(
      parseInt(parts[0]),
      parseInt(parts[1]) - 1,
      parseInt(parts[2])
    );

    // Get today as local date (no time component)
    const today = new Date();
    const todayLocal = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const diffTime = departure.getTime() - todayLocal.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return "overdue";
    // All other cases are now urgent (previously soon and normal)
    return "urgent";
  };

  const getAccountHolderName = (
    primaryAccountHolder: string | { name?: string; email?: string }
  ): string => {
    if (typeof primaryAccountHolder === "string") {
      return primaryAccountHolder;
    }
    return (
      primaryAccountHolder?.name || primaryAccountHolder?.email || "Unknown"
    );
  };

  const applyFilters = () => {
    if (!dashboardData?.pendingConsultations) {
      setFilteredConsultations([]);
      return;
    }

    let filtered = [...dashboardData.pendingConsultations];

    // Filter by urgency
    if (filters.urgency !== "all") {
      filtered = filtered.filter((consultation) => {
        const urgencyLevel = getUrgencyLevel(consultation.departureDate);
        return urgencyLevel === filters.urgency;
      });
    }

    // Filter by priority
    if (filters.priority !== "all") {
      filtered = filtered.filter(
        (consultation) =>
          consultation.priority.toLowerCase() === filters.priority
      );
    }

    // Filter by status
    if (filters.status !== "all") {
      filtered = filtered.filter((consultation) => {
        const hasStatus =
          filters.status === "pending"
            ? consultation.formsStatus.pending > 0
            : filters.status === "under_review"
            ? (consultation.formsStatus.underReview ||
                consultation.formsStatus.under_review) > 0
            : filters.status === "completed"
            ? consultation.formsStatus.completed > 0
            : true;
        return hasStatus;
      });
    }

    // Filter by search query
    if (filters.search.trim() !== "") {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((consultation) => {
        // Search in consultation number
        if (consultation.consultationNumber.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Search in account holder name
        const accountHolder = getAccountHolderName(consultation.primaryAccountHolder);
        if (accountHolder.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Search in traveler names
        if (consultation.travelers) {
          const travelerMatch = consultation.travelers.some((traveler) => {
            const firstName = traveler.first_name || traveler.firstName || "";
            const lastName = traveler.last_name || traveler.lastName || "";
            const fullName = `${firstName} ${lastName}`.toLowerCase();
            return fullName.includes(searchLower);
          });
          if (travelerMatch) return true;
        }

        // Search in destination countries and cities
        if (consultation.destinations) {
          try {
            const destinationsArray = getDestinationsArray(consultation.destinations);
            const locationMatch = destinationsArray.some((dest) => {
              const country = (dest.destination_country || "").toLowerCase();
              const city = (dest.destination_city || "").toLowerCase();
              return country.includes(searchLower) || city.includes(searchLower);
            });
            if (locationMatch) return true;
          } catch (error) {
            console.error("Error parsing destinations for search:", error);
          }
        }

        return false;
      });
    }

    setFilteredConsultations(filtered);
  };

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "all" || value === "") {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams());
  };


  const fetchDestinationInfo = async (
    consultationId: string,
    destinations: string | Destination[]
  ) => {
    const destinationsArray = getDestinationsArray(destinations);
    if (destinationsArray.length === 0) return;

    const uniqueCountries = Array.from(
      new Set(
        destinationsArray.map((d) => d.destination_country).filter(Boolean)
      )
    );

    setLoadingDestinations((prev) => ({ ...prev, [consultationId]: true }));

    try {
      const infoPromises = uniqueCountries.map(async (country) => {
        try {
          const info = await getDestinationInfoByCountry(country, 1);
          return { country, info };
        } catch (error) {
          console.error(`Failed to fetch info for ${country}:`, error);
          return { country, info: null };
        }
      });

      const results = await Promise.all(infoPromises);
      const newInfoMap = { ...destinationInfoMap };

      results.forEach(({ country, info }) => {
        if (info) {
          newInfoMap[country] = info;
        }
      });

      setDestinationInfoMap(newInfoMap);
    } catch (error) {
      console.error("Error fetching destination info:", error);
    } finally {
      setLoadingDestinations((prev) => ({ ...prev, [consultationId]: false }));
    }
  };

  const handleToggleRecommendations = (
    consultationId: string,
    destinations: string | Destination[]
  ) => {
    if (showRecommendations === consultationId) {
      setShowRecommendations(null);
    } else {
      setShowRecommendations(consultationId);
      const destinationsArray = getDestinationsArray(destinations);
      const countries = destinationsArray
        .map((d) => d.destination_country)
        .filter(Boolean);
      const needsLoading = countries.some(
        (country) => !destinationInfoMap[country]
      );

      if (needsLoading) {
        fetchDestinationInfo(consultationId, destinations);
      }

      const newExpanded: Record<string, boolean> = {};
      countries.forEach((country) => {
        newExpanded[country] = true;
      });
      setExpandedCountries(newExpanded);
    }
  };

  const toggleCountryExpansion = (country: string) => {
    setExpandedCountries((prev) => ({
      ...prev,
      [country]: !prev[country],
    }));
  };

  const refreshDashboard = () => {
    setHasFetched(false);
    setLoading(true);
    fetchDashboard();
  };

  return {
    dashboardData,
    filteredConsultations,
    loading,
    error,
    showRecommendations,
    destinationInfoMap,
    loadingDestinations,
    expandedCountries,
    filters,
    handleToggleRecommendations,
    toggleCountryExpansion,
    updateFilter,
    resetFilters,
    getUrgencyLevel,
    refreshDashboard,
  };
}
